<template>
    <v-dialog v-model="dialogSwitch" persistent scrollable width="75%">
        <v-card>
            <v-card-title id="heading" class="d-flex justify-space-between align-center">
                <div class="d-flex align-center ga-3">
                    <h4>Campaign Devices: {{ campaign.name }}</h4>
                    <v-text-field
                        v-model="searchQuery"
                        density="compact"
                        hide-details
                        placeholder="Search serial..."
                        prepend-inner-icon="mdi-magnify"
                        variant="outlined"
                        style="max-width: 220px;"
                        clearable
                        @update:model-value="onSearch"
                    />
                    <v-btn icon="mdi-refresh" size="x-small" variant="tonal" @click="fetchDevicesStatus"/>
                </div>
                <v-btn density="compact" icon="mdi-close" variant="flat"
                       @click="$emit('update:modelValue', false)"/>
            </v-card-title>

            <v-container>
                <!-- Status filter chips -->
                <v-row class="mb-1">
                    <v-col cols="12" class="d-flex align-center ga-2 flex-wrap">
                        <v-chip
                            v-for="chip in statusChips"
                            :key="chip.value"
                            :color="activeStatusFilter === chip.value ? chip.color : undefined"
                            :variant="activeStatusFilter === chip.value ? 'flat' : 'outlined'"
                            size="small"
                            class="cursor-pointer"
                            @click="toggleStatusFilter(chip.value)"
                        >
                            {{ chip.label }}: {{ chip.count }}
                        </v-chip>
                        <v-btn
                            v-if="(statusCounts[6] ?? 0) > 0"
                            color="error"
                            size="small"
                            variant="tonal"
                            prepend-icon="mdi-refresh"
                            class="ml-2"
                            :loading="requeueing"
                            @click="requeueAllFailed"
                        >
                            Requeue Failed ({{ statusCounts[6] ?? 0 }})
                        </v-btn>
                        <v-chip v-if="autoRefreshActive" size="x-small" color="success" variant="tonal" class="ml-auto">
                            <v-icon start size="12">mdi-autorenew</v-icon>Auto-refresh
                        </v-chip>
                    </v-col>
                </v-row>

                <v-row>
                    <v-col cols="12">
                        <v-table>
                            <thead>
                            <tr>
                                <th class="cursor-pointer" @click="toggleSort('device')">
                                    Device <v-icon v-if="sortBy === 'device'" :icon="sortIcon" size="x-small"/>
                                </th>
                                <th class="cursor-pointer" @click="toggleSort('file_name')">
                                    File <v-icon v-if="sortBy === 'file_name'" :icon="sortIcon" size="x-small"/>
                                </th>
                                <th>Current Version</th>
                                <th class="cursor-pointer" @click="toggleSort('component')">
                                    Component Downloading <v-icon v-if="sortBy === 'component'" :icon="sortIcon" size="x-small"/>
                                </th>
                                <th class="cursor-pointer" @click="toggleSort('attempts')">
                                    Attempts <v-icon v-if="sortBy === 'attempts'" :icon="sortIcon" size="x-small"/>
                                </th>
                                <th class="cursor-pointer" @click="toggleSort('is_downloading')">
                                    Downloading <v-icon v-if="sortBy === 'is_downloading'" :icon="sortIcon" size="x-small"/>
                                </th>
                                <th class="cursor-pointer" @click="toggleSort('download_percentage')">
                                    Download Progress <v-icon v-if="sortBy === 'download_percentage'" :icon="sortIcon" size="x-small"/>
                                </th>
                                <th class="cursor-pointer" @click="toggleSort('status')">
                                    Status <v-icon v-if="sortBy === 'status'" :icon="sortIcon" size="x-small"/>
                                </th>
                                <th class="cursor-pointer" @click="toggleSort('updated_at')">
                                    Last Updated <v-icon v-if="sortBy === 'updated_at'" :icon="sortIcon" size="x-small"/>
                                </th>
                                <th>Action</th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr
                                v-for="(status, index) in devicesStatus"
                                v-if="!loading && devicesStatus.length"
                                :key="index"
                                class="text-left"
                            >
                                <td>
                                    <div class="d-flex align-center ga-1">
                                        <v-icon v-bind="signalIcon(status.signal_bars ?? 0)" size="x-small"/>
                                        <v-icon v-if="isMotionFresh(status.device_last_update) && status.motion_state" icon="mdi-car-arrow-right" color="green" size="small" title="Moving"/>
                                        <v-icon v-else-if="isRecentlyActive(status.device_last_update)" icon="mdi-car-brake-parking" color="grey" size="small" title="Parked"/>
                                        <v-icon v-else icon="mdi-signal-off" color="red-lighten-2" size="x-small" title="Offline"/>
                                        <a v-if="status.device" :href="`/devices/${status.device}`" target="_blank" class="text-primary text-decoration-none">
                                            {{ status.device }}
                                        </a>
                                        <span v-else>-</span>
                                    </div>
                                    <div v-if="status.asset_name" class="text-caption text-medium-emphasis">
                                        {{ status.asset_name }}
                                    </div>
                                </td>
                                <td>{{ status.file_name }}</td>
                                <td>{{ status.current_version }}</td>
                                <td>{{ status.component }}</td>
                                <td>{{ status.attempts }}</td>
                                <td>
                                    <v-icon :icon="`mdi-${status.is_downloading?'check':'close'}-circle`"/>
                                </td>
                                <td>
                                    <v-progress-linear
                                        :model-value="status.download_percentage"
                                        height="20"
                                        style="cursor: pointer"
                                        @click="showTimeline(status)">
                                        <small :class="{'text-white': (status.download_percentage>35)}">{{
                                                Math.ceil(status.download_percentage)
                                            }}%</small>
                                    </v-progress-linear>
                                </td>
                                <td>
                                    <v-tooltip location="top" max-width="320">
                                        <template v-slot:activator="{ props }">
                                            <v-chip v-bind="props" :color="statusColor(status.status)" size="x-small" variant="tonal" label>
                                                {{ statusLabel(status.status) }}
                                            </v-chip>
                                        </template>
                                        <span>{{ statusTooltip(status.status) }}</span>
                                    </v-tooltip>
                                </td>
                                <td>{{ formatDateTime(status.updated_at) }}</td>
                                <td>
                                    <v-btn v-if="status.status !== 'DISABLED'" class="text-indigo" size="x-small"
                                           variant="outlined"
                                           @click="updateStatus(status,1)">
                                        Requeue
                                    </v-btn>
                                </td>
                            </tr>
                            <tr v-else>
                                <td v-if="loading" class="text-center" colspan="10">
                                    <v-progress-circular color="primary" indeterminate/>
                                </td>
                                <td v-else class="text-center" colspan="10">No record found</td>
                            </tr>
                            </tbody>
                        </v-table>
                    </v-col>
                </v-row>

                <v-row v-if="totalPages > 1" justify="center">
                    <v-col cols="8">
                        <v-pagination
                            v-model="currentPage"
                            :length="totalPages"
                            class="my-4"
                            density="compact"
                            @update:model-value="fetchDevicesStatus"
                        ></v-pagination>
                    </v-col>
                </v-row>
            </v-container>

            <v-card-actions class="px-4 d-flex justify-end">
                <v-btn
                    size="small"
                    type="button"
                    @click="$emit('update:modelValue', false);"
                >
                    Close
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>

    <DownloadTimeline
        v-model="timelineDialogSwitch"
        :campaign-id="campaign?.id"
        :device-id="timelineDeviceId"
        :device-slno="timelineDeviceSlno"/>
</template>

<script setup>
import {ref, computed, onMounted, onBeforeUnmount} from "vue";
import {send} from "@/composables/apiClient";
import {useSnackbarStore} from "@/stores/snackBarStore";
import {formatDateTime} from '@/composables/useTimezone';
import DownloadTimeline from "@/components/dialogs/ota/DownloadTimeline.vue";

const {showSnackbar} = useSnackbarStore();
const props = defineProps({
    modelValue: Boolean,
    campaign: Object,
});
const dialogSwitch = ref(props.modelValue);
const loading = ref(true);
const totalPages = ref(1);
const currentPage = ref(1);
const devicesStatus = ref([]);
const searchQuery = ref('');
const searchTimeout = ref(null);
const sortBy = ref('updated_at');
const sortDir = ref('desc');
const sortIcon = computed(() => sortDir.value === 'asc' ? 'mdi-arrow-up' : 'mdi-arrow-down');

// Status filter
const activeStatusFilter = ref(null);
const statusCounts = ref({});
const requeueing = ref(false);

const statusLabels = {
    0: {label: 'Disabled', color: 'grey'},
    1: {label: 'Ready', color: 'blue-lighten-1'},
    2: {label: 'Queued', color: 'indigo'},
    3: {label: 'Acked', color: 'blue'},
    4: {label: 'Downloading', color: 'orange'},
    5: {label: 'Downloaded', color: 'teal'},
    6: {label: 'Failed', color: 'error'},
    7: {label: 'Upgraded', color: 'success'},
};

const statusChips = computed(() => {
    const all = Object.entries(statusLabels).map(([value, meta]) => ({
        value: parseInt(value),
        label: meta.label,
        color: meta.color,
        count: statusCounts.value[value] ?? 0,
    }));
    // Show "All" chip first, then only statuses with count > 0
    const total = Object.values(statusCounts.value).reduce((a, b) => a + b, 0);
    return [
        {value: null, label: 'All', color: 'primary', count: total},
        ...all.filter(c => c.count > 0),
    ];
});

// Map API enum names to pretty labels + colors
const enumNameMap = {
    DISABLED: {label: 'Disabled', color: 'grey'},
    READY_TO_QUEUE: {label: 'Ready', color: 'blue-lighten-1'},
    QUEUED: {label: 'Queued', color: 'indigo'},
    QUEUE_ACKED: {label: 'Acked', color: 'blue'},
    DOWNLOADING: {label: 'Downloading', color: 'orange'},
    DOWNLOAD_COMPLETED: {label: 'Downloaded', color: 'teal'},
    FAILED: {label: 'Failed', color: 'error'},
    UPGRADED: {label: 'Upgraded', color: 'success'},
};
const statusLabel = (name) => enumNameMap[name]?.label ?? name;
const statusColor = (name) => enumNameMap[name]?.color ?? 'grey';

// Plain-language explanation of each OTA status, shown on hover over the chip.
const statusTooltips = {
    DISABLED: 'This device is excluded from the campaign. The gateway will not queue any OTA for it until the campaign or this device is re-enabled.',
    READY_TO_QUEUE: 'Waiting for the next uplink from this device. On the next packet the gateway will piggyback an OTA_REQUEST on the server ACK.',
    QUEUED: 'OTA_REQUEST is queued for delivery to the device and waiting for the next outbound opportunity.',
    QUEUE_ACKED: 'Device acknowledged the OTA_REQUEST and is about to start (or has started) the HTTP download.',
    DOWNLOADING: 'Legacy state. The gateway no longer transitions through this. Treat as Queue Acked if you see it.',
    DOWNLOAD_COMPLETED: 'Device finished the HTTP download and is now verifying the checksum and writing the image to flash.',
    FAILED: 'Device reported an OTA failure. Common causes are a download timeout, a checksum mismatch, or a flash write error. Use Requeue to retry.',
    UPGRADED: 'OTA succeeded. The device rebooted into the new firmware version and reported success.',
};
const statusTooltip = (name) => statusTooltips[name] ?? 'Status unknown.';

const toggleStatusFilter = (value) => {
    activeStatusFilter.value = activeStatusFilter.value === value ? null : value;
    currentPage.value = 1;
    fetchDevicesStatus();
};

const requeueAllFailed = () => {
    const count = statusCounts.value[6] ?? 0;
    if (!confirm(`Are you sure you want to requeue ${count} failed device(s)? They will re-attempt the OTA download.`)) return;
    requeueing.value = true;
    send("post", `admin/ota/campaigns/${props.campaign.id}/requeue-failed`)
        .then((response) => {
            if (response.data.success) {
                showSnackbar(response.data.message ?? "Failed devices requeued");
                fetchDevicesStatus();
            }
        })
        .catch((error) => {
            showSnackbar(error.response?.data?.message ?? "Failed to requeue devices", "error");
        })
        .finally(() => requeueing.value = false);
};

// Auto-refresh every 30 seconds
let autoRefreshTimer = null;
const autoRefreshActive = ref(true);
const startAutoRefresh = () => {
    stopAutoRefresh();
    autoRefreshTimer = setInterval(() => {
        if (!loading.value) fetchDevicesStatus();
    }, 30000);
};
const stopAutoRefresh = () => {
    if (autoRefreshTimer) {
        clearInterval(autoRefreshTimer);
        autoRefreshTimer = null;
    }
};

const toggleSort = (column) => {
    if (sortBy.value === column) {
        sortDir.value = sortDir.value === 'asc' ? 'desc' : 'asc';
    } else {
        sortBy.value = column;
        sortDir.value = 'asc';
    }
    currentPage.value = 1;
    fetchDevicesStatus();
};

const onSearch = () => {
    clearTimeout(searchTimeout.value);
    searchTimeout.value = setTimeout(() => {
        currentPage.value = 1;
        fetchDevicesStatus();
    }, 400);
};

const timelineDialogSwitch = ref(false);
const timelineDeviceId = ref(null);
const timelineDeviceSlno = ref('');

const showTimeline = (status) => {
    timelineDeviceId.value = status.device_id;
    timelineDeviceSlno.value = status.device || ''; // device field is the slno string
    timelineDialogSwitch.value = true;
};

const emits = defineEmits(["update:modelValue"]);
onMounted(() => {
    fetchDevicesStatus();
    startAutoRefresh();
});
onBeforeUnmount(() => {
    stopAutoRefresh();
    clearTimeout(searchTimeout.value);
});

// Same signal icon logic as Asset Index and DeviceList screens
const signalIcon = (bars) => {
    switch (bars) {
        case 1: return { icon: 'mdi-signal-cellular-outline', color: '#d70000' };
        case 2: return { icon: 'mdi-signal-cellular-1', color: '#ff9e01' };
        case 3: return { icon: 'mdi-signal-cellular-2', color: '#849e00' };
        case 4: case 5: return { icon: 'mdi-signal-cellular-3', color: '#099f00' };
        default: return { icon: 'mdi-signal-off', color: '#999' };
    }
};
const isRecentlyActive = (lastUpdate) => {
    if (!lastUpdate) return false;
    const diff = (Date.now() - new Date(lastUpdate + 'Z').getTime()) / 1000;
    return diff < 1800; // 30 minutes
};
const isMotionFresh = (lastUpdate) => {
    if (!lastUpdate) return false;
    const diff = (Date.now() - new Date(lastUpdate + 'Z').getTime()) / 1000;
    return diff < 180; // 3 minutes - motion state goes stale quickly
};

const fetchDevicesStatus = () => {
    loading.value = true;
    const params = {
        page: currentPage.value,
        sort_by: sortBy.value,
        sort_dir: sortDir.value,
    };
    if (searchQuery.value) params.search = searchQuery.value;
    if (activeStatusFilter.value !== null) params.status = activeStatusFilter.value;
    send("get", `admin/ota/campaigns/devices-status/${props.campaign.id}`, params).then((response) => {
        devicesStatus.value = response.data.data.data;
        totalPages.value = response.data.data.meta.last_page;
        currentPage.value = response.data.data.meta.current_page;
        if (response.data.data.status_counts) {
            statusCounts.value = response.data.data.status_counts;
        }
    }).catch((error) => {
        showSnackbar(error.response?.data?.message ?? "Campaign devices status could not be loaded", "error");
    }).finally(() => loading.value = false);
}
const updateStatus = (campaignStatus, status = 0) => {
    if (!confirm(`Are you sure you want to update ota status to: "${status === 0 ? 'Disable' : 'Requeue'}" for Device: "${campaignStatus.device}"?`)) {
        return;
    }
    loading.value = true;
    send("put", `admin/ota/campaigns/update-devices-status/${campaignStatus.id}`, {status: status})
        .then((response) => {
            if (response.data.success) {
                showSnackbar(response.data.message ?? "Campaign status updated successfully");
                fetchDevicesStatus();
            }
        }).catch((error) => {
        showSnackbar(error.response?.data?.message ?? "Campaign status could not be updated", "error");
    }).finally(() => loading.value = false);
}
</script>

<style scoped>
table th {
    font-weight: bold !important;
}
</style>
